import { useState, useEffect } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface NotificationPreferences {
  alignmentReminders: boolean;
  energyAlerts: boolean;
  updates: boolean;
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  alignmentReminders: true,
  energyAlerts: true,
  updates: true
};

export function useNotifications() {
  const [enabled, setEnabled] = useLocalStorage<boolean>('notifications', true);
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES);
  const { currentUser } = useAuth();
  
  useEffect(() => {
    if (!currentUser) return;

    // Load notification preferences from Supabase
    const loadPreferences = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('notification_preferences')
          .eq('id', currentUser.id)
          .single();

        if (error) throw error;
        if (data?.notification_preferences) {
          setPreferences(data.notification_preferences);
        }
      } catch (error) {
        console.error('Error loading notification preferences:', error);
      }
    };

    loadPreferences();
  }, [currentUser]);
  
  const requestPermission = async () => {
    if (!('Notification' in window)) {
      return false;
    }
    
    if (Notification.permission === 'granted') {
      return true;
    }
    
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    
    return false;
  };

  const toggleNotifications = async () => {
    const hasPermission = await requestPermission();
    setEnabled(hasPermission);
    return hasPermission;
  };

  const updatePreferences = async (newPreferences: Partial<NotificationPreferences>) => {
    if (!currentUser) return false;

    try {
      const updatedPreferences = { ...preferences, ...newPreferences };
      
      const { error } = await supabase
        .from('profiles')
        .update({ notification_preferences: updatedPreferences })
        .eq('id', currentUser.id);

      if (error) throw error;
      
      setPreferences(updatedPreferences);
      return true;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      return false;
    }
  };

  return { 
    enabled, 
    preferences,
    toggleNotifications,
    updatePreferences
  };
}